/**
 * Página de listagem de classificações - Modo PREVISÃO
 * Sempre navega para ListaProdutoPrevisaoPage
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Layout, Loading, ErrorMessage, SearchBar, PainelPrevisao } from '@components';
import { ListaClassificacao } from '@components/classificacao/ListaClassificacao';
import { useFetch, useFilter, useDebounce } from '@hooks';
import { classificacaoController } from '@controllers';
import { useSessionContext } from '@contexts';
import { Classificacao } from '@types';
import theme from '@theme';

type OrderBy = 'nome-asc' | 'nome-desc' | 'total-asc' | 'total-desc';

export const ListaClassificacaoPrevisaoPage: React.FC = () => {
  const navigate = useNavigate();
  const { setLastClassificacao } = useSessionContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState<OrderBy>('nome-asc');

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Carrega classificações
  const { data: classificacoes, isLoading, error, refetch } = useFetch(
    () => classificacaoController.obterClassificacoes()
  );

  // Filtra por busca
  const { filteredData } = useFilter(classificacoes || [], ['nome']);

  // Ordena
  const sortedData = React.useMemo(() => {
    if (!filteredData) return [];

    const [field, direction] = orderBy.split('-') as [
      'nome' | 'total',
      'asc' | 'desc'
    ];

    if (field === 'nome') {
      return classificacaoController.ordenarPorNome(filteredData, direction);
    } else {
      return classificacaoController.ordenarPorTotal(filteredData, direction);
    }
  }, [filteredData, orderBy]);

  const handleSelectClassificacao = (classificacao: Classificacao) => {
    // Salva última classificação acessada
    setLastClassificacao(classificacao.codigo);

    // SEMPRE navega para produtos de PREVISÃO
    console.log('ListaClassificacaoPrevisao - Navegando para:', `/produto-previsao/${classificacao.codigo}`);
    navigate(`/produto-previsao/${classificacao.codigo}`);
  };

  if (isLoading) {
    return (
      <Layout title="Categorias" showPainelPrevisao>
        <PainelPrevisao />
        <Loading message="Carregando categorias..." />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout title="Categorias" showPainelPrevisao>
        <PainelPrevisao />
        <ErrorMessage
          title="Erro ao carregar categorias"
          message={error.message}
          onRetry={refetch}
        />
      </Layout>
    );
  }

  return (
    <Layout title="Previsão de Compra" showHeader showFooter footerType="default" showPainelPrevisao>
      <PainelPrevisao />
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar categoria..."
          />
        </Box>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Ordenar por</InputLabel>
          <Select
            value={orderBy}
            label="Ordenar por"
            onChange={(e) => setOrderBy(e.target.value as OrderBy)}
            sx={{ bgcolor: theme.palette.elite.contrastText }}
          >
            <MenuItem value="nome-asc">Nome (A-Z)</MenuItem>
            <MenuItem value="nome-desc">Nome (Z-A)</MenuItem>
            <MenuItem value="total-desc">Mais produtos</MenuItem>
            <MenuItem value="total-asc">Menos produtos</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <ListaClassificacao
        classificacoes={sortedData}
        onSelectClassificacao={handleSelectClassificacao}
        isSearching={!!debouncedSearch}
      />
    </Layout>
  );
};

export default ListaClassificacaoPrevisaoPage;
